import { useState, useEffect } from 'react';
import { commentDmAPI, accountAPI } from '../../services/api';

const EMPTY_FORM = {
  mediaId: '',
  keywords: '',
  privateReplyMessage: '',
  followerMessage: '',
  nonFollowerMessage: '',
};

function CommentDmPage() {
  const [account, setAccount] = useState(null);
  const [rules, setRules] = useState([]);
  const [posts, setPosts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // 규칙 추가/수정 모달
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [postSearch, setPostSearch] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const accountRes = await accountAPI.getMy();
      if (accountRes.success && accountRes.data?.length) {
        const myAccount = accountRes.data.find(a => a.status === 'ACTIVE' && a.platformUserId) || accountRes.data[0];
        setAccount(myAccount);

        const [rulesRes, postsRes, logsRes] = await Promise.all([
          commentDmAPI.getRules(myAccount.id),
          commentDmAPI.getPosts(myAccount.id),
          commentDmAPI.getLogs(myAccount.id),
        ]);
        if (rulesRes.success) setRules(rulesRes.data || []);
        if (postsRes.success) setPosts(postsRes.data || []);
        if (logsRes.success) setLogs(logsRes.data || []);
      }
    } catch (err) {
      console.error('댓글 DM 설정 로드 실패:', err);
      setError('설정을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingRule(null);
    setForm(EMPTY_FORM);
    setPostSearch('');
    setShowModal(true);
  };

  const openEditModal = (rule) => {
    setEditingRule(rule);
    setForm({
      mediaId: rule.mediaId || '',
      keywords: rule.keywords || '',
      privateReplyMessage: rule.privateReplyMessage || '',
      followerMessage: rule.followerMessage || '',
      nonFollowerMessage: rule.nonFollowerMessage || '',
    });
    setPostSearch('');
    setShowModal(true);
  };

  const handleSaveRule = async () => {
    if (!account) return;
    if (!form.keywords.trim() || !form.privateReplyMessage.trim()
        || !form.followerMessage.trim() || !form.nonFollowerMessage.trim()) {
      setError('키워드와 메시지 3종을 모두 입력해주세요.');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        accountId: account.id,
        mediaId: form.mediaId || null,
        keywords: form.keywords.trim(),
        privateReplyMessage: form.privateReplyMessage.trim(),
        followerMessage: form.followerMessage.trim(),
        nonFollowerMessage: form.nonFollowerMessage.trim(),
      };
      const res = editingRule
        ? await commentDmAPI.updateRule(editingRule.id, payload)
        : await commentDmAPI.createRule(payload);
      if (res.success) {
        await loadData();
        setShowModal(false);
        showSuccessMsg(editingRule ? '규칙이 수정되었습니다.' : '규칙이 추가되었습니다.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRule = async (ruleId) => {
    try {
      const res = await commentDmAPI.toggleRule(ruleId);
      if (res.success) {
        setRules(rules.map(r => (r.id === ruleId ? res.data : r)));
        showSuccessMsg(res.data.enabled ? '규칙이 활성화되었습니다.' : '규칙이 비활성화되었습니다.');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!confirm('이 규칙을 삭제하시겠습니까?')) return;
    try {
      const res = await commentDmAPI.deleteRule(ruleId);
      if (res.success) {
        setRules(rules.filter(r => r.id !== ruleId));
        showSuccessMsg('규칙이 삭제되었습니다.');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const showSuccessMsg = (msg) => {
    setSuccess(msg);
    setError(null);
    setTimeout(() => setSuccess(null), 3000);
  };

  const shortId = (mediaId) => `게시물 ${String(mediaId).slice(-8)}`;

  const truncateTitle = (title, len) => (title.length > len ? title.slice(0, len) + '…' : title);

  const postLabel = (mediaId) => {
    if (!mediaId) return '전체 게시물';
    const post = posts.find(p => p.mediaId === mediaId);
    if (!post) return shortId(mediaId);
    return post.title ? truncateTitle(post.title, 20) : shortId(mediaId);
  };

  // 퍼널 집계 (재시도 루프 중복 방지: 댓글/사용자 단위 distinct)
  const distinct = (rows) => new Set(rows.map(l => l.commentId || l.igsid)).size;
  const funnel = {
    detected: distinct(logs.filter(l => l.step === 'COMMENT_DETECTED' && l.status === 'SUCCESS')),
    replied: distinct(logs.filter(l => l.step === 'PRIVATE_REPLY' && l.status === 'SUCCESS')),
    userReplied: distinct(logs.filter(l => l.step === 'USER_REPLIED')),
    follower: distinct(logs.filter(l => l.step === 'FOLLOWER_CHECK' && l.followerResult === 'FOLLOWER')),
    infoSent: distinct(logs.filter(l => l.step === 'INFO_SENT' && l.followerResult === 'FOLLOWER' && l.status === 'SUCCESS')),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-slate-400 text-lg">로딩 중...</div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <span className="material-symbols-outlined text-6xl text-slate-600">person_off</span>
        <p className="text-slate-400 text-lg">연결된 인스타그램 계정이 없습니다</p>
        <p className="text-slate-500 text-sm">자동화 설정에서 계정을 먼저 등록해주세요</p>
      </div>
    );
  }

  return (
    <div>
      {/* 헤더 */}
      <div className="flex flex-col gap-1 sm:gap-2 mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight">댓글 DM 자동화</h2>
        <p className="text-slate-400 text-sm sm:text-base">
          게시물 댓글 키워드에 반응해 DM을 보내고, 팔로워를 확인한 뒤 정보를 전달합니다.
        </p>
      </div>

      {/* 알림 메시지 */}
      {error && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            <span className="material-symbols-outlined text-sm">close</span>
          </button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-500/10 border border-green-500/30 text-green-400 rounded-xl">
          {success}
        </div>
      )}

      {/* 동작 안내 카드 */}
      <div className="p-4 sm:p-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 mb-6 sm:mb-8">
        <div className="flex items-center gap-4">
          <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <span className="material-symbols-outlined text-primary text-2xl">forum</span>
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
            <span className="font-bold text-slate-700 dark:text-slate-200">동작 순서:</span>{' '}
            댓글 키워드 감지 → 댓글 작성자에게 DM 발송(게시물당 1회) → 사용자가 답장하면 팔로우 여부 확인 →
            팔로워에게 정보 발송 / 비팔로워에게 팔로우 유도.
            사용자가 답장해야 다음 단계가 진행됩니다.
          </div>
        </div>
      </div>

      {/* 규칙 관리 */}
      <section className="flex flex-col gap-4 mb-8">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <span className="material-symbols-outlined text-primary">rule</span>
            자동화 규칙
          </h3>
          <button
            onClick={openAddModal}
            className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg text-sm font-bold hover:bg-primary/90 transition-all"
          >
            <span className="material-symbols-outlined text-sm">add</span> 규칙 추가
          </button>
        </div>

        {/* 모바일: 카드 리스트 */}
        <div className="sm:hidden space-y-2">
          {rules.length > 0 ? (
            rules.map(rule => (
              <div key={rule.id} className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0" onClick={() => openEditModal(rule)}>
                    <p className="text-xs text-slate-500">{postLabel(rule.mediaId)}</p>
                    <p className="text-sm font-bold text-primary">{rule.keywords}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{rule.privateReplyMessage}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => handleToggleRule(rule.id)}
                      className={rule.enabled ? 'text-green-500' : 'text-slate-500'}>
                      <span className="material-symbols-outlined text-[20px]">{rule.enabled ? 'toggle_on' : 'toggle_off'}</span>
                    </button>
                    <button onClick={() => handleDeleteRule(rule.id)} className="text-slate-400 hover:text-red-500">
                      <span className="material-symbols-outlined text-[20px]">delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-6 text-center text-sm text-slate-500 border border-dashed border-slate-300 dark:border-slate-700 rounded-xl">
              등록된 규칙이 없습니다
            </div>
          )}
        </div>

        {/* 데스크탑: 테이블 */}
        <div className="hidden sm:block overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 font-semibold">대상 게시물</th>
                <th className="px-4 py-3 font-semibold">트리거 키워드</th>
                <th className="px-4 py-3 font-semibold">첫 DM 문구</th>
                <th className="px-4 py-3 font-semibold text-center">상태</th>
                <th className="px-4 py-3 font-semibold text-right">관리</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rules.length > 0 ? (
                rules.map(rule => (
                  <tr key={rule.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                    <td className="px-4 py-4 text-slate-600 dark:text-slate-300">{postLabel(rule.mediaId)}</td>
                    <td className="px-4 py-4 font-medium text-primary">{rule.keywords}</td>
                    <td className="px-4 py-4 text-slate-600 dark:text-slate-300 max-w-[280px] truncate">{rule.privateReplyMessage}</td>
                    <td className="px-4 py-4 text-center">
                      <button onClick={() => handleToggleRule(rule.id)}
                        className={`text-3xl leading-none align-middle ${rule.enabled ? 'text-green-500' : 'text-slate-500'}`}>
                        <span className="material-symbols-outlined">{rule.enabled ? 'toggle_on' : 'toggle_off'}</span>
                      </button>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <button onClick={() => openEditModal(rule)} className="text-slate-400 hover:text-primary transition-colors mr-2">
                        <span className="material-symbols-outlined">edit</span>
                      </button>
                      <button onClick={() => handleDeleteRule(rule.id)} className="text-slate-400 hover:text-red-500 transition-colors">
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                    등록된 규칙이 없습니다. "규칙 추가" 버튼으로 시작하세요.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* 퍼널 통계 */}
      <section className="flex flex-col gap-4">
        <h3 className="text-xl font-bold flex items-center gap-2">
          <span className="material-symbols-outlined text-primary">filter_alt</span>
          전환 퍼널
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: '댓글 감지', value: funnel.detected, icon: 'chat_bubble' },
            { label: 'DM 발송', value: funnel.replied, icon: 'send' },
            { label: '사용자 답장', value: funnel.userReplied, icon: 'reply' },
            { label: '팔로워 확인', value: funnel.follower, icon: 'how_to_reg' },
            { label: '정보 발송', value: funnel.infoSent, icon: 'task_alt' },
          ].map(item => (
            <div key={item.label} className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 flex flex-col items-center gap-1">
              <span className="material-symbols-outlined text-primary">{item.icon}</span>
              <p className="text-2xl font-black">{item.value}</p>
              <p className="text-xs text-slate-500">{item.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 규칙 추가/수정 모달 */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div
            className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-6 flex flex-col gap-4"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold">{editingRule ? '규칙 수정' : '규칙 추가'}</h3>

            {/* 모달 위에서 발생한 에러는 페이지 배너가 오버레이에 가려지므로 모달 안에도 표시 */}
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl text-sm flex items-center justify-between">
                <span>{error}</span>
                <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
                  <span className="material-symbols-outlined text-sm">close</span>
                </button>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-slate-500 dark:text-slate-400">대상 게시물</label>

              {/* 게시물 많으면 캡션으로 검색 */}
              {posts.length > 6 && (
                <div className="relative">
                  <span className="material-symbols-outlined absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
                  <input
                    type="text"
                    className="w-full rounded-lg bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 py-2 pl-8 pr-3 text-sm"
                    placeholder="캡션으로 게시물 검색..."
                    value={postSearch}
                    onChange={e => setPostSearch(e.target.value)}
                  />
                </div>
              )}

              {/* 썸네일 그리드 선택기 */}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto p-2 rounded-lg bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700">
                {/* 전체 게시물 타일 */}
                <button
                  type="button"
                  onClick={() => setForm({ ...form, mediaId: '' })}
                  className={`aspect-square rounded-lg flex flex-col items-center justify-center gap-1 text-xs font-semibold border-2 transition-all ${form.mediaId === '' ? 'border-primary bg-primary/10 text-primary' : 'border-transparent bg-slate-200 dark:bg-slate-700 text-slate-500 hover:border-slate-400'}`}
                >
                  <span className="material-symbols-outlined">apps</span>
                  전체
                </button>

                {/* 수정 중인 규칙의 게시물이 목록에 없으면(삭제됨) 명시 타일 */}
                {form.mediaId && !posts.find(p => p.mediaId === form.mediaId) && (
                  <div className="aspect-square rounded-lg flex flex-col items-center justify-center gap-1 text-[10px] border-2 border-primary bg-primary/10 text-primary text-center p-1">
                    <span className="material-symbols-outlined">image_not_supported</span>
                    삭제된 게시물
                  </div>
                )}

                {posts
                  .filter(p => {
                    const q = postSearch.trim().toLowerCase();
                    if (!q) return true;
                    return (p.title || '').toLowerCase().includes(q) || String(p.mediaId).includes(q);
                  })
                  .map(p => {
                    const selected = form.mediaId === p.mediaId;
                    return (
                      <button
                        type="button"
                        key={p.mediaId}
                        onClick={() => setForm({ ...form, mediaId: p.mediaId })}
                        title={p.title || shortId(p.mediaId)}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${selected ? 'border-primary ring-2 ring-primary/40' : 'border-transparent hover:border-slate-400'}`}
                      >
                        {p.thumbnailPath ? (
                          <img src={p.thumbnailPath} alt="" loading="lazy" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-slate-200 dark:bg-slate-700 text-[10px] text-slate-500 p-1 text-center break-all">
                            {shortId(p.mediaId)}
                          </div>
                        )}
                        {selected && (
                          <div className="absolute top-1 right-1 size-5 rounded-full bg-primary flex items-center justify-center shadow">
                            <span className="material-symbols-outlined text-white text-[14px]">check</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
              </div>

              <p className="text-xs text-slate-400">
                {form.mediaId ? `선택됨: ${postLabel(form.mediaId)}` : '전체 게시물에 적용됩니다'}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-slate-500 dark:text-slate-400">트리거 키워드 (공백 구분)</label>
              <input
                type="text"
                className="w-full rounded-lg bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 p-3"
                placeholder="예: 나도 정보 신청"
                value={form.keywords}
                onChange={e => setForm({ ...form, keywords: e.target.value })}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                첫 DM 문구 <span className="font-normal">(답장을 유도해야 다음 단계가 진행됩니다)</span>
              </label>
              <textarea
                rows={3}
                className="w-full rounded-lg bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 p-3 resize-none"
                placeholder="안녕하세요! 아무 답장이나 보내주시면 바로 안내드릴게요 🙂 (자동응답)"
                value={form.privateReplyMessage}
                onChange={e => setForm({ ...form, privateReplyMessage: e.target.value })}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-slate-500 dark:text-slate-400">팔로워에게 보낼 정보 메시지</label>
              <textarea
                rows={3}
                className="w-full rounded-lg bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 p-3 resize-none"
                placeholder="감사합니다! 요청하신 정보입니다: ..."
                value={form.followerMessage}
                onChange={e => setForm({ ...form, followerMessage: e.target.value })}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-slate-500 dark:text-slate-400">비팔로워에게 보낼 팔로우 유도 메시지</label>
              <textarea
                rows={3}
                className="w-full rounded-lg bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 p-3 resize-none"
                placeholder="팔로우 후 아무 메시지나 보내주시면 바로 보내드릴게요!"
                value={form.nonFollowerMessage}
                onChange={e => setForm({ ...form, nonFollowerMessage: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-2 mt-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg text-sm font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
              >
                취소
              </button>
              <button
                onClick={handleSaveRule}
                disabled={saving}
                className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-bold hover:bg-primary/90 transition-all disabled:opacity-50"
              >
                {saving ? '저장 중...' : (editingRule ? '수정' : '추가')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommentDmPage;
